import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, {useContext, useEffect, useState} from 'react';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {ArbeitszeitService, OpenAPI, UserService} from '../../../../../services/openapi';
import {Edit} from '@mui/icons-material';
import {get} from 'http';

interface IMitarbeiterEintrag {
  user: any;
  setEdit: (value: string) => void;
}

function MitarbeiterEintrag(props: IMitarbeiterEintrag) {
  const {user, setEdit} = props;
  const [hoover, setHoover] = useState(false);

  return (
    <Box
      sx={{
        minHeight: 40,
        height: 40,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      onMouseEnter={() => setHoover(true)}
      onMouseLeave={() => setHoover(false)}>
      <Box sx={{width: 40, display: 'flex', justifyContent: 'center'}}>
        {hoover && (
          <IconButton size='small' onClick={() => setEdit(user.user_id)}>
            <Edit fontSize='small' />
          </IconButton>
        )}
      </Box>

      <Box sx={{width: 200, overflow: 'hidden', textOverflow: 'ellipsis'}}>
        {user.fullname ? user.fullname : user.email}
      </Box>

      <Box sx={{width: 100, textAlign: 'center'}}>{user.zeiterfassung_bearbeiter}</Box>

      <Box sx={{width: 100, textAlign: 'center'}}>{user.zeiterfassung_abteilung}</Box>

      <Box sx={{width: 120, textAlign: 'center'}}>{user.employee_number || ''}</Box>

      <Box sx={{width: 100, textAlign: 'center'}}>{user.total_vacation_days || 0}</Box>

      <Box sx={{width: 100, textAlign: 'center'}}>{user.initial_remaining_days || 0}</Box>

      <Box sx={{width: 100, textAlign: 'center'}}>{user.monthly_vacation_days ? 'Ja' : 'Nein'}</Box>
    </Box>
  );
}

interface IWorkingDataSetBearbeiter {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function WorkingDataSetBearbeiter(props: IWorkingDataSetBearbeiter) {
  const {call_backend_api} = useContext(APIWrapperContext);
  const {open, setOpen} = props;
  const [mitarbeiter, setMitarbeiter] = useState<any[]>();
  const [edit_mitarbeiter, setEditMitarbeiter] = useState<string | undefined>(undefined);
  const [tmp_kuerzel, setTmpKuerzel] = useState<string | undefined>(undefined);
  const [tmp_abteilung, setTmpAbteilung] = useState<string | undefined>(undefined);
  const [tmp_employee_number, setTmpEmployeeNumber] = useState<string | undefined>(undefined);
  const [tmp_total_days, setTmpTotalDays] = useState<number | undefined>(undefined);
  const [tmp_remaining_days, setTmpRemainingDays] = useState<number | undefined>(undefined);
  const [tmp_monthly_vacation_days, setTmpMonthlyVacationDays] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setOpen(false);
    setEditMitarbeiter(undefined);
    setError(null);
  };

  const cancelEdit = () => {
    setEditMitarbeiter(undefined);
    setError(null);
  };

  const updateMitarbeiterList = () => {
    Promise.all([
      new Promise<any[]>((resolve) => {
        call_backend_api(
          ArbeitszeitService.getBearbeiterForUsersArbeitzeitDashboardWorkerGetBearbeiterGet(),
          (res: any) => resolve(res),
          () => resolve([]),
          undefined,
          false
        );
      }),
      new Promise<any[]>((resolve) => {
        call_backend_api(
          UserService.getAllVacationDaysUserGetAllVacationDaysGet(),
          (res: any) => resolve(res),
          () => resolve([]),
          undefined,
          false
        );
      }),
      new Promise<any>((resolve) => {
        call_backend_api(
          UserService.getAllEmployeeNumbersUserGetAllEmployeeNumbersGet(),
          (res: any) => resolve(res),
          () => resolve({}),
          undefined,
          false
        );
      }),
    ]).then(([users, vacationDays, employeeNumbers]) => {
      const vacationMap = new Map<string, any>(
        (vacationDays as any[]).map((v: any) => [v.user_id, v])
      );

      const mergedUsers = (users as any[]).map((user: any) => ({
        ...user,
        ...(vacationMap.get(user.user_id) || {}),
        employee_number: employeeNumbers[user.user_id] || '',
      }));

      const sortedUsers = mergedUsers.sort((a, b) => {
        // Extrahiere Nachnamen aus fullname oder verwende email falls kein fullname vorhanden
        const getLastName = (user: any) => {
          if (!user.fullname) return user.email;
          // Teile den vollständigen Namen und nimm den letzten Teil als Nachnamen
          const nameParts = user.fullname.split(' ');
          return nameParts[nameParts.length - 1];
        };

        const lastNameA = getLastName(a);
        const lastNameB = getLastName(b);

        return lastNameA.localeCompare(lastNameB, 'de'); // Verwende deutsche Lokalisierung für Umlaute
      });

      setMitarbeiter(sortedUsers);
    });
  };

  useEffect(() => {
    if (open) {
      updateMitarbeiterList();
    }
  }, [open]);

  useEffect(() => {
    if (edit_mitarbeiter) {
      const el = mitarbeiter?.find((el) => el.user_id === edit_mitarbeiter);
      setTmpKuerzel(el?.zeiterfassung_bearbeiter);
      setTmpAbteilung(el?.zeiterfassung_abteilung);
      setTmpEmployeeNumber(el?.employee_number || '');
      setTmpTotalDays(el?.total_vacation_days);
      setTmpRemainingDays(el?.initial_remaining_days);
      setTmpMonthlyVacationDays(el?.monthly_vacation_days || false);
    }
  }, [edit_mitarbeiter]);

  const handleEdit = () => {
    if (
      !edit_mitarbeiter ||
      !tmp_kuerzel ||
      !tmp_abteilung ||
      tmp_total_days === undefined ||
      tmp_remaining_days === undefined
    ) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    setError(null);

    // First update the bearbeiter and abteilung
    call_backend_api(
      ArbeitszeitService.setBearbeiterForUserArbeitzeitDashboardWorkerSetBearbeiterPost(
        edit_mitarbeiter,
        tmp_kuerzel,
        tmp_abteilung
      ),
      () => {
        // Next update the vacation days and monthly_vacation_days
        call_backend_api(
          UserService.setVacationDaysUserSetVacationDaysPost(
            edit_mitarbeiter,
            tmp_total_days,
            undefined,
            undefined,
            tmp_remaining_days,
            tmp_monthly_vacation_days
          ),
          () => {
            // Finally update the employee number
            call_backend_api(
              UserService.setEmployeeNumberUserSetEmployeeNumberPost(
                edit_mitarbeiter,
                tmp_employee_number || ''
              ),
              () => {
                updateMitarbeiterList();
                setEditMitarbeiter(undefined);
              },
              undefined,
              undefined,
              true
            );
          },
          undefined,
          undefined,
          true
        );
      },
      undefined,
      undefined,
      true
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
      <DialogTitle>Bearbeiter-Kürzel, Personalnummer und Urlaubstage definieren</DialogTitle>
      <DialogContent sx={{maxHeight: '80vh', overflow: 'hidden'}}>
        <DialogContentText sx={{mb: 2}}>
          Bearbeiten Sie hier die Mitarbeiterdaten. <br />
          Aktivieren Sie "Monatlicher Urlaub", um Urlaubstage monatlich zu verteilen (1/12 des
          Gesamturlaubs pro Monat).
        </DialogContentText>

        {error && (
          <Typography color='error' sx={{mb: 2}}>
            {error}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            fontWeight: 'bold',
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            alignItems: 'center',
          }}>
          <Box sx={{width: 40}}></Box>
          <Box sx={{width: 200}}>Mitarbeiter/-in</Box>
          <Box sx={{width: 100, textAlign: 'center'}}>Kürzel</Box>
          <Box sx={{width: 100, textAlign: 'center'}}>Abteilung</Box>
          <Box sx={{width: 120, textAlign: 'center'}}>Personalnummer</Box>
          <Box sx={{width: 100, textAlign: 'center'}}>Gesamturlaub</Box>
          <Box sx={{width: 100, textAlign: 'center'}}>Resturlaub</Box>
          <Box sx={{width: 100, textAlign: 'center'}}>Monatlicher Urlaub</Box>
        </Box>

        <Box sx={{overflowY: 'auto', maxHeight: 'calc(80vh - 180px)'}}>
          {mitarbeiter?.map((el) => (
            <React.Fragment key={el.user_id}>
              {edit_mitarbeiter !== el.user_id && (
                <MitarbeiterEintrag user={el} setEdit={setEditMitarbeiter} />
              )}

              {edit_mitarbeiter === el.user_id && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    backgroundColor: 'action.hover',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                  <Box sx={{width: 40}}></Box>
                  <Box sx={{width: 200}}>{el.fullname ? el.fullname : el.email}</Box>

                  <TextField
                    sx={{width: 100, mx: 0.5}}
                    size='small'
                    value={tmp_kuerzel || ''}
                    onChange={(e) => setTmpKuerzel(e.target.value)}
                    inputProps={{maxLength: 3}}
                    variant='outlined'
                  />

                  <TextField
                    sx={{width: 100, mx: 0.5}}
                    size='small'
                    value={tmp_abteilung || ''}
                    onChange={(e) => setTmpAbteilung(e.target.value)}
                    inputProps={{maxLength: 3}}
                    variant='outlined'
                  />

                  <TextField
                    sx={{width: 120, mx: 0.5}}
                    size='small'
                    value={tmp_employee_number || ''}
                    onChange={(e) => setTmpEmployeeNumber(e.target.value)}
                    variant='outlined'
                    placeholder='Personalnummer'
                  />

                  <TextField
                    sx={{width: 100, mx: 0.5}}
                    size='small'
                    type='number'
                    value={tmp_total_days ?? ''}
                    onChange={(e) => {
                      const newTotal = parseFloat(e.target.value) || 0;
                      setTmpTotalDays(newTotal);
                    }}
                    variant='outlined'
                  />

                  <TextField
                    sx={{width: 100, mx: 0.5}}
                    size='small'
                    type='number'
                    value={tmp_remaining_days ?? ''}
                    onChange={(e) => {
                      const newRemaining = parseFloat(e.target.value) || 0;
                      setTmpRemainingDays(newRemaining);
                    }}
                    variant='outlined'
                  />

                  <Box sx={{width: 100, textAlign: 'center'}}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={tmp_monthly_vacation_days}
                          onChange={(e) => setTmpMonthlyVacationDays(e.target.checked)}
                        />
                      }
                      label=''
                      sx={{margin: 0}}
                    />
                  </Box>

                  <Box sx={{display: 'flex', gap: 1, ml: 1}}>
                    <Button variant='contained' onClick={handleEdit} size='small'>
                      Speichern
                    </Button>
                    <Button variant='outlined' onClick={cancelEdit} size='small'>
                      Abbrechen
                    </Button>
                  </Box>
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
